import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Grid,
  useMediaQuery,
  useTheme, MenuItem, TextField
} from '@mui/material';
import { FiPlus, FiTrash2, FiGift, FiArrowLeft, FiEdit } from 'react-icons/fi';
import VoucherFormModal from './VoucherFormModal';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import Swal from 'sweetalert2';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import StatusToggle from './VouchersList/StatusToggle';

const AdminVouchers = ({ title, filterStatus, icon }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [selectedExam, setSelectedExam] = useState('');
  const [selectedExamObj, setSelectedExamObj] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedSubjectObj, setSelectedSubjectObj] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedTopicObj, setSelectedTopicObj] = useState(null);

  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);

  //fetch exams
  useEffect(() => {
    // axios.get(`${API_URL}/api/exams`)
    api.get('/exams')
      .then(res => setExams(res.data))
      .catch(err => console.error('Error fetching exams:', err));
  }, []);

  //fetch subjects
  useEffect(() => {
    if (selectedExam) {
      // axios.get(`${API_URL}/api/subjects?exam_id=${selectedExam}`)
      api.get(`/subjects?exam_id=${selectedExam}`)
        .then(res => setSubjects(res.data))
        .catch(err => console.error('Error fetching subjects:', err));
    } else {
      setSubjects([]);
    }
  }, [selectedExam]);

  //fetch topics
  useEffect(() => {
    if (selectedSubject) {
      // axios.get(`${API_URL}/api/topics?subject_id=${selectedSubject}`)
      api.get(`/topics?subject_id=${selectedSubject}`)
        .then(res => setTopics(res.data))
        .catch(err => console.error('Error fetching topics:', err));
    } else {
      setTopics([]);
    }
  }, [selectedSubject]);

  const [vouchers, setVouchers] = useState([]);

  //api for vouchers get method  
  useEffect(() => {
    //axios.get(`${API_URL}/api/vouchers`)
    api.get('/vouchers')
      .then(res => {
        console.log('API Response:', res.data);
        let formattedData = res.data.map(voucher => ({
          id: voucher.id,
          exam: voucher.exam_name,
          subject: voucher.subject_name,
          topic: voucher.topic_name,
          code: voucher.code,
          validDays: voucher.usage_limit,
          expiryDate: voucher.expiry_date,
          status: voucher.status
        }));


        // Apply filter
        if (filterStatus) {
          formattedData = formattedData.filter(v => v.status === filterStatus);
        }

        setVouchers(formattedData);

        //  NEW: counts
        const total = res.data.data.length;
        const active = res.data.data.filter(v => v.status === "active").length;
        const inactive = res.data.data.filter(v => v.status === "inactive").length;

        // If your dashboard SummaryCards is in parent, you can lift these counts up via props or global state
        console.log("Voucher Counts =>", { total, active, inactive });
      })
      .catch(err => console.error('Error fetching vouchers:', err));
  }, [filterStatus]);



  const handleCreate = (newVoucher) => {
    setVouchers([...vouchers, { id: Date.now(), ...newVoucher }]);
    setShowModal(false);
  };

  //function for handling delete
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to delete this voucher?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        api.delete(`/vouchers/${id}`)
          .then(res => {
            Swal.fire(
              'Deleted!',
              res.data.message,
              'success'
            );
            // Remove from state after successful soft delete
            setVouchers(prev => prev.filter(v => v.id !== id));
          })
          .catch(err => {
            console.error('Error deleting voucher:', err);
            Swal.fire(
              'Error!',
              'Failed to delete voucher.',
              'error'
            );
          });
      }
    });
  };


  //function for handling bulk delete
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      Swal.fire('No vouchers selected', 'Please select at least one voucher to delete.', 'info');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete ${selectedRows.length} selected voucher(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Send delete requests for all selected vouchers
          await Promise.all(
            selectedRows.map((id) => api.delete(`/vouchers/${id}`))
          );

          // Update state after successful deletion
          setVouchers(prev => prev.filter(v => !selectedRows.includes(v.id)));
          setSelectedRows([]);

          Swal.fire(
            'Deleted!',
            `${selectedRows.length} voucher(s) have been deleted.`,
            'success'
          );
        } catch (err) {
          console.error('Error deleting vouchers:', err);
          Swal.fire('Error', 'Failed to delete some or all vouchers.', 'error');
        }
      }
    });
  };


  const columns = [
    { field: 'exam', headerName: 'Exam', flex: 0.6 },
    { field: 'subject', headerName: 'Subject', flex: 1 },
    { field: 'topic', headerName: 'Topic', flex: 1 },
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'validDays', headerName: 'Valid Days', flex: 0.4 },
    // { field: 'fromDate', headerName: 'From Date', flex: 1 },
    // { field: 'toDate', headerName: 'To Date', flex: 1 },
    { field: 'expiryDate', headerName: 'Expiry Date', flex: 0.5 },

    // {
    //   field: 'actions',
    //   headerName: 'Actions',
    //   flex: 0.5,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Box display="flex" gap={1}>
    //       <StatusToggle
    //         id={params.row.id}
    //         status={params.row.status}
    //         onUpdate={(id, newStatus) => {
    //           // Update local vouchers state
    //           setVouchers(prev =>
    //             prev.map(v => v.id === id ? { ...v, status: newStatus } : v)
    //           );
    //         }}
    //       />
    //       <Tooltip title="Delete">
    //         <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
    //           <FiTrash2 />
    //         </IconButton>
    //       </Tooltip>
    //     </Box>
    //   )
    // }

    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {!params.row.is_validated && (
            <>
              <StatusToggle
                id={params.row.id}
                status={params.row.status}
                onUpdate={(id, newStatus) => {
                  setVouchers(prev =>
                    prev.map(v => v.id === id ? { ...v, status: newStatus } : v)
                  );
                }}
              />
              <Tooltip title="Delete">
                <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
                  <FiTrash2 />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }

  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: 'vouchers_data' }} />
      {selectedRows.length > 0 && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleBulkDelete}
          sx={{ ml: 2 }}
        >
          Delete Selected ({selectedRows.length})
        </Button>
      )}
    </GridToolbarContainer>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh', // full height layout
        padding: 2,
        boxSizing: 'border-box'
      }}
    >
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap">
        {/* Back Button */}
        <Grid item xs={12} sm="auto">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: '12px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #00bcd4, #007bff)',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                transform: 'scale(1.1)',
                boxShadow: '0 6px 15px rgba(0, 123, 255, 0.4)',
              }
            }}
          >
            <FiArrowLeft size={20} />
            {!isSmallScreen && (
              <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>
                Back
              </Typography>
            )}
          </IconButton>
        </Grid>

        {/* Centered Title */}
        <Grid item xs={12} sm>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              textAlign: 'center',
              mb: isSmallScreen ? 2 : 0
            }}
          >
            <Box
              sx={{
                fontSize: '2rem',
                color: '#1976d2',
                background: '#e3f2fd',
                borderRadius: '50%',
                padding: '0.5rem',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                animation: 'pulseIcon 1.6s infinite',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#0d47a1',
                  boxShadow: '0 0 12px rgba(13, 71, 161, 0.5)',
                },
              }}
            >
              <FiGift />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textTransform: 'uppercase',
                letterSpacing: '1.2px',
                px: 1,
                animation: 'slideIn 1s ease-in-out',
                '&:hover': {
                  textShadow: '0 0 10px rgba(0, 123, 255, 0.7)',
                }
              }}
            >
              All Vouchers
            </Typography>
          </Box>
        </Grid>

        {/* Create Button */}
        <Grid item xs={12} sm="auto">
          <Button
            variant="contained"
            color="warning"
            startIcon={<FiPlus />}
            fullWidth={isSmallScreen}
            onClick={() => setShowModal(true)}
          >
            Create Voucher
          </Button>
        </Grid>
      </Grid>


      {/* code for dropdowns of exam subject and topic */}
      <Grid container spacing={2} mb={4}>

        {/* <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="Select Exam (Required)"
            value={selectedExam}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedExam(id);
              setSelectedSubject('');
              setSelectedTopic('');
              const examObj = exams.find(ex => ex.id === id);
              setSelectedExamObj(examObj || null);
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selectedExam) return "Select Exam (Required)";
                if (selected === "") return "None";
                const examObj = exams.find(ex => ex.id === selected);
                return examObj?.title || "";
              },
            }}
          >
            <MenuItem value="">None</MenuItem>
            {exams.length > 0
              ? exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.title}
                </MenuItem>
              ))
              : <MenuItem disabled>No exams found</MenuItem>
            }
          </TextField>
        </Grid> */}

        {/* Subject Dropdown */}
        {/* <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="Select Subject (Optional)"
            value={selectedSubject}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedSubject(id);
              const subjObj = subjects.find(sub => sub.id === id);
              setSelectedSubjectObj(subjObj || null);
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selectedSubject) return "Select Subject (Optional)";
                if (selected === "") return "None";
                const subjObj = subjects.find(sub => sub.id === selected);
                return subjObj?.name || "";
              },
            }}
          >
            <MenuItem value="">None</MenuItem>
            {subjects.map((subject) => (
              <MenuItem key={subject.id} value={subject.id}>
                {subject.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid> */}

        {/* Topic Dropdown */}
        {/* <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="Select Topic (Optional)"
            value={selectedTopic}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedTopic(id);
              const topicObj = topics.find((t) => t.id === id);
              setSelectedTopicObj(topicObj || null);
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selectedTopic) return "Select Topic (Optional)";
                if (selected === "") return "None";
                const topicObj = topics.find((t) => t.id === selected);
                return topicObj?.title || "";
              },
            }}
          >
            <MenuItem value="">None</MenuItem>
            {topics.map((topic) => (
              <MenuItem key={topic.id} value={topic.id}>
                {topic.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid> */}

        {/* Export CSV Button */}
        {/* <Grid
          item xs={12}
          sm={3}
          sx={{
            display: 'flex',
            alignItems: 'center', // vertical center
            justifyContent: 'flex-end', // keep it right-aligned
          }}
        >
          <Tooltip title="Download data as CSV" arrow>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<FileDownloadIcon />} // Add icon here
              onClick={() => {
                console.log("Exporting CSV...");
              }}
            >
              Export CSV
            </Button>
          </Tooltip>
        </Grid> */}
      </Grid>


      {/* DataGrid */}
      <Box sx={{
        width: '100%',
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        backgroundColor: '#fff',
        borderRadius: 2
      }}>
        <DataGrid
          rows={vouchers}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={5}
          rowsPerPageOptions={[5, 10]}
          pagination
          disableSelectionOnClick
          checkboxSelection
          onSelectionModelChange={(ids) => setSelectedRows(ids)}
          components={{
            Toolbar: CustomToolbar
          }}
          sx={{
            flexGrow: 1,
            fontFamily: 'Segoe UI',
            border: '1px solid #ddd',
            boxShadow: 2,
            '& .MuiDataGrid-columnHeaders': {
              background: 'linear-gradient(to right, #e0f3ff, #d5faff)',
              color: '#004c99',
              fontWeight: 'bold',
              fontSize: '1rem'
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.95rem'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#fef7e0'
            },
            '& .MuiIconButton-root': {
              color: '#007BFF',
              '&:hover': {
                transform: 'scale(1.2)',
                color: '#00b3e6',
              }
            }
          }}
        />
      </Box>

      {/* Modal */}
      {showModal && (
        <VoucherFormModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreate}
        />
      )}
    </Box>
  );
};

export default AdminVouchers;





